/**
 * Клавиатура: основной ввод, а не удобство.
 *
 * На скорости мышь проигрывает. Пока рука тянется к строке, лента успевает
 * переехать, а вилка — умереть; стрелка и enter работают за то время, за
 * которое глаз ещё держит строку. Поэтому стрелки двигают выделение, enter
 * раскрывает, esc закрывает, плюс и минус двигают единственный регулятор.
 *
 * Вход в вилку тоже с клавиш, и это не украшение: две трети денег ленты лежат в
 * вилках ниже 7%, а они живут 10–15 секунд. Путь «нашёл глазами → довёл курсор →
 * попал в кнопку» этот пласт не берёт вовсе.
 *
 * * `1`…`3` — открыть контору этой ноги, сумма уходит в буфер.
 * * пробел — следующий шаг вилки: открыть, потом отметить «поставил», и так по
 *   ногам. Ровно то же делает пробел в витрине: два разных смысла у одной
 *   клавиши на соседних экранах — это ошибка человека под секундомером.
 * * `c` — сумма ближайшей неотмеченной ноги в буфер ещё раз.
 * * `f` — заморозить ленту и отпустить её.
 *
 * Действие без выделения берёт ВЕРХНЮЮ строку и сразу её выделяет с заморозкой:
 * иначе следующее нажатие уедет в другую вилку, потому что лента под руками
 * успела переехать.
 *
 * Все клавиши обрабатываются только когда фокус не в поле ввода: иначе минус в
 * поле банка уводил бы порог, и человек не понял бы, что произошло.
 */

import { useEffect, useRef } from 'react';

import { useSettings } from '../store/settings';
import { enter, legsOf, pasteOf, placed } from './act';
import { stepGate } from './gate';
import { useShow } from './show';
import { copy } from './Grab';
import type { FeedRow } from './rows';
import { blankStep, useScreen } from './store';

const inField = (target: EventTarget | null): boolean =>
  target instanceof HTMLElement &&
  (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);

/** Счёт конторы открыт — клавиши ленты молчат, даже если фокус на кнопке. */
export function keysQuiet(target: EventTarget | null = null): boolean {
  if (inField(target)) return true;
  return document.querySelector('.nx-bked-veil[role="dialog"]') !== null;
}

/** Соседняя строка. Пустой список и отсутствие выделения дают первую. */
export function step(ids: readonly string[], current: string | null, delta: number): string | null {
  if (ids.length === 0) return null;
  const at = current === null ? -1 : ids.indexOf(current);
  if (at < 0) return delta > 0 ? ids[0]! : ids[ids.length - 1]!;
  const next = Math.min(ids.length - 1, Math.max(0, at + delta));
  return ids[next]!;
}

/** Ближайшая неотмеченная нога: с ней и работают клавиши входа. */
export function nextLeg(row: FeedRow, steps: ReturnType<typeof blankStep>): 1 | 2 | 3 {
  const stamps = [steps.first, steps.second, steps.third ?? null];
  const count = legsOf(row).length;
  for (let i = 0; i < count; i += 1) {
    if (stamps[i] === null) return (i + 1) as 1 | 2 | 3;
  }
  return count as 1 | 2 | 3;
}

export function useKeys(rows: readonly FeedRow[]): void {
  // Список строк меняется каждый кадр ленты. Подписка на него означала бы
  // снятие и постановку слушателя несколько раз в секунду, поэтому слушатель
  // ставится один раз, а актуальный список читается из ссылки.
  const feed = useRef(rows);
  feed.current = rows;

  useEffect(() => {
    /**
     * Строка, с которой работают клавиши входа. Без выделения берётся верхняя,
     * и она же сразу выделяется с заморозкой: без этого второе нажатие уедет в
     * другую вилку — лента под руками успевает переехать.
     */
    const aimed = (): FeedRow | undefined => {
      const screen = useScreen.getState();
      const rows = feed.current;
      const found = screen.sel === null ? rows[0] : rows.find((row) => row.id === screen.sel);
      if (found === undefined) return undefined;
      if (screen.sel !== found.id) {
        screen.setSel(found.id);
        screen.freeze(rows.map((row) => row.id));
      }
      return found;
    };

    const onKey = (e: KeyboardEvent): void => {
      if (keysQuiet(e.target)) return;
      // Сочетания с модификаторами принадлежат браузеру и системе: Ctrl+C
      // обязан копировать выделенный текст, а не сумму ноги.
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const screen = useScreen.getState();
      const settings = useSettings.getState();
      const ids = feed.current.map((row) => row.id);

      switch (e.key) {
        case 'ArrowDown':
        case 'ArrowUp': {
          e.preventDefault();
          const next = step(ids, screen.sel, e.key === 'ArrowDown' ? 1 : -1);
          screen.setSel(next);
          if (next !== null) {
            // Выделение морозит порядок: человек целится именно в эту строку.
            screen.freeze(ids);
            document.querySelector(`[data-id="${next}"]`)?.scrollIntoView({ block: 'nearest' });
          }
          return;
        }
        case 'Enter': {
          if (screen.sel === null) return;
          e.preventDefault();
          screen.setOpen(screen.open === screen.sel ? null : screen.sel);
          return;
        }
        case 'Escape': {
          e.preventDefault();
          // Сначала закрывается карточка, и только вторым нажатием снимается
          // выделение: одна клавиша, отменяющая сразу всё, отменяет больше
          // задуманного.
          if (screen.open !== null) screen.setOpen(null);
          else {
            screen.setSel(null);
            screen.thaw();
          }
          return;
        }
        case ' ': {
          // Шаг вилки. Открытая вкладка — не деньги на кону, поэтому «поставил»
          // отмечается отдельным нажатием: иначе тот, кто ткнул из
          // любопытства, получал бы измеренный темп из воздуха.
          e.preventDefault();
          const row = aimed();
          if (row === undefined) return;
          const steps = useScreen.getState().steps[row.id] ?? blankStep();
          const index = nextLeg(row, steps);
          if (steps.opened[index - 1] === null) enter(row, index, settings.pairOpen);
          else placed(row, index);
          return;
        }
        case '1':
        case '2':
        case '3': {
          e.preventDefault();
          const row = aimed();
          if (row === undefined) return;
          const index = Number(e.key) as 1 | 2 | 3;
          if (legsOf(row).length < index) return;
          enter(row, index, settings.pairOpen);
          return;
        }
        case 'c':
        case 'C':
        case 'с':
        case 'С': {
          // Русская «с» на той же клавише: раскладку в момент ставки не
          // переключают, и молчащая клавиша здесь читается как поломка.
          e.preventDefault();
          const row = aimed();
          if (row === undefined) return;
          const steps = useScreen.getState().steps[row.id] ?? blankStep();
          const leg = legsOf(row)[nextLeg(row, steps) - 1];
          if (leg !== undefined) void copy(pasteOf(leg));
          return;
        }
        case 'f':
        case 'F':
        case 'а':
        case 'А': {
          e.preventDefault();
          if (screen.frozen) screen.thaw();
          else screen.freeze(ids);
          return;
        }
        case '+':
        case '=':
          e.preventDefault();
          settings.setGate(stepGate(settings.gate, true, useShow.getState().unit, settings.bank, settings.fx));
          return;
        case '-':
        case '_':
          e.preventDefault();
          settings.setGate(stepGate(settings.gate, false, useShow.getState().unit, settings.bank, settings.fx));
          return;
        default:
          return;
      }
    };

    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);
}
